import type { DynamicClient } from '@dynamic-labs-sdk/client';
import {
  assertDefined,
  consumeMfaTokenIfRequiredForAction,
  formatWalletProviderGroupKey,
  formatWalletProviderKey,
  getActiveNetworkIdFromLastKnownRegistry,
  getSignedSessionId,
  switchActiveNetworkInLastKnownRegistry,
} from '@dynamic-labs-sdk/client/core';
import type { BitcoinNetwork } from '@dynamic-labs-sdk/client/waas/core';
import {
  DYNAMIC_WAAS_METADATA,
  createWaasProvider,
  getAllUserWaasAddressesForChain,
} from '@dynamic-labs-sdk/client/waas/core';
import { MFAAction, WalletProviderEnum } from '@dynamic-labs/sdk-api-core';
import { Psbt, networks } from 'bitcoinjs-lib';

import type { BitcoinSendTransactionResponse } from '../../../BitcoinWalletProvider.types';
import { bitcoinExecuteCheckoutTransaction } from '../../../utils/bitcoinExecuteCheckoutTransaction';
import { bitcoinTransferAmount } from '../../../utils/bitcoinTransferAmount';
import type {
  WaasBitcoinTransaction,
  WaasBitcoinWalletProvider,
} from '../../WaasBitcoinWalletProvider.types';
import { broadcastTransaction } from '../broadcastTransaction';
import { buildPsbt as buildPsbtUtil } from '../buildPsbt';
import { getPublicKeyForWalletAccount } from '../getPublicKeyForWalletAccount/getPublicKeyForWalletAccount';

export const createWalletProviderForWaasBitcoin = (
  sdkClient: DynamicClient
): WaasBitcoinWalletProvider => {
  const chain = 'BTC' as const;

  const walletProviderType = WalletProviderEnum.EmbeddedWallet;

  const key = formatWalletProviderKey({
    chain,
    displayName: DYNAMIC_WAAS_METADATA.displayName,
    walletProviderType,
  });

  const waasProvider = createWaasProvider({ chain, sdkClient });

  const getActiveNetworkId: WaasBitcoinWalletProvider['getActiveNetworkId'] =
    async () =>
      getActiveNetworkIdFromLastKnownRegistry({
        client: sdkClient,
        walletProviderKey: key,
      });

  const switchActiveNetwork: WaasBitcoinWalletProvider['switchActiveNetwork'] =
    async ({ networkId }) =>
      switchActiveNetworkInLastKnownRegistry({
        client: sdkClient,
        networkId,
        walletProviderKey: key,
      });

  const getConnectedAddresses: WaasBitcoinWalletProvider['getConnectedAddresses'] =
    async () => {
      const waasBitcoinAddresses = getAllUserWaasAddressesForChain(
        { chain },
        sdkClient
      );

      return {
        addresses: waasBitcoinAddresses,
      };
    };

  const signPsbt: WaasBitcoinWalletProvider['signPsbt'] = async ({
    request,
    walletAccount,
  }) => {
    assertDefined(walletAccount, 'Wallet account is required');

    const waasClient = await waasProvider.getWaasClient();

    const { signature: signedSessionId } = await getSignedSessionId(sdkClient);

    const mfaToken = await consumeMfaTokenIfRequiredForAction(
      { mfaAction: MFAAction.WalletWaasSign },
      sdkClient
    );

    const signedPsbt = await waasClient.signTransaction({
      authToken: sdkClient.token ?? undefined,
      mfaToken,
      senderAddress: walletAccount.address,
      signedSessionId,
      transaction: request.unsignedPsbtBase64,
    });

    return { signedPsbt };
  };

  const signPsbts: WaasBitcoinWalletProvider['signPsbts'] = async ({
    requests,
    walletAccount,
  }) => {
    assertDefined(walletAccount, 'Wallet account is required');

    const signedPsbts = await Promise.all(
      requests.map(async (request) => {
        const { signedPsbt } = await signPsbt({ request, walletAccount });

        return signedPsbt;
      })
    );

    return { signedPsbts };
  };

  const signMessage: WaasBitcoinWalletProvider['signMessage'] = async ({
    message,
    walletAccount,
  }) => {
    assertDefined(walletAccount, 'Wallet account is required');

    const waasClient = await waasProvider.getWaasClient();

    const { signature: signedSessionId } = await getSignedSessionId(sdkClient);

    const mfaToken = await consumeMfaTokenIfRequiredForAction(
      { mfaAction: MFAAction.WalletWaasSign },
      sdkClient
    );

    const signature = await waasClient.signMessage({
      accountAddress: walletAccount.address,
      authToken: sdkClient.token ?? undefined,
      bitcoinConfig: {
        network: 'mainnet' as BitcoinNetwork,
      },
      message,
      mfaToken,
      signedSessionId,
    });

    return { signature };
  };

  const buildPsbt: WaasBitcoinWalletProvider['buildPsbt'] = async ({
    transaction,
    walletAccount,
  }) => {
    assertDefined(walletAccount, 'Wallet account is required');

    const publicKeyHex = getPublicKeyForWalletAccount(walletAccount, sdkClient);

    return buildPsbtUtil({
      accountAddress: walletAccount.address,
      amountInSatoshis: transaction.amount,
      feePriority: transaction.feePriority ?? 'medium',
      network: networks.bitcoin,
      publicKeyHex,
      recipientAddress: transaction.recipientAddress,
    });
  };

  const sendBitcoin: WaasBitcoinWalletProvider['sendBitcoin'] = async ({
    transaction,
    walletAccount,
  }): Promise<BitcoinSendTransactionResponse> => {
    assertDefined(walletAccount, 'Wallet account is required');

    const waasTransaction: WaasBitcoinTransaction = {
      amount: transaction.amount,
      recipientAddress: transaction.recipientAddress,
    };

    const unsignedPsbt = await buildPsbt({
      transaction: waasTransaction,
      walletAccount,
    });

    const { signedPsbt } = await signPsbt({
      request: { allowedSighash: [], unsignedPsbtBase64: unsignedPsbt },
      walletAccount,
    });

    const psbt = Psbt.fromBase64(signedPsbt, { network: networks.bitcoin });

    psbt.finalizeAllInputs();

    const rawTx = psbt.extractTransaction().toHex();

    const transactionId = await broadcastTransaction(rawTx);

    return { transactionId };
  };

  return {
    ...waasProvider,
    buildPsbt,
    chain,
    executeCheckoutTransaction: (args) =>
      bitcoinExecuteCheckoutTransaction(args, sdkClient),
    getActiveNetworkId,
    getConnectedAddresses,
    groupKey: formatWalletProviderGroupKey(DYNAMIC_WAAS_METADATA.displayName),
    key,
    metadata: {
      displayName: DYNAMIC_WAAS_METADATA.displayName,
      icon: DYNAMIC_WAAS_METADATA.icon,
    },
    sendBitcoin,
    signMessage,
    signPsbt,
    signPsbts,
    switchActiveNetwork,
    transferAmount: (args) => bitcoinTransferAmount(args, sdkClient),
    walletProviderType,
  };
};
